<script setup>
import { ref, inject, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import powerAgentsApi from 'dashboard/api/powerAgents';
import SectionShell from './SectionShell.vue';
import { useRascunhoDaSecao } from '../useRascunhoDaSecao';
import GuardrailsPanel from '../../agentShared/GuardrailsPanel.vue';

const props = defineProps({
  agent: { type: Object, required: true },
});

const { t } = useI18n();
const ctx = inject('powerAgentContext');

const guardrails = ref(props.agent.guardrails || []);
// Quais rails nascem ligados, vindo do servidor (`PowerAgent::GUARDRAILS_PADRAO`). Serve ao
// aviso de risco quando alguém desliga um deles — e vem de lá, e não de uma cópia aqui,
// porque lista de segurança duplicada diverge calada.
const padrao = ref([]);
const loading = ref(false);
const saving = ref(false);
// Types edited since the last save — only these are persisted on Save, so we never
// create rows for guardrails the user never touched.
const dirty = ref(new Set());
// Resultado de "testar antes de ligar", por tipo. Fica só na tela: testar não salva nada.
const testState = ref({});

async function fetchGuardrails() {
  loading.value = true;
  try {
    const data = await powerAgentsApi.guardrailsIndex(
      ctx.accountId(),
      ctx.agentId()
    );
    guardrails.value = data.guardrails || [];
    padrao.value = data.padrao || [];
  } catch (e) {
    // keep whatever the show payload provided
  } finally {
    loading.value = false;
  }
}

// Edits stay local until Save — no visual "did it save?" ambiguity, matching the
// other sections. The panel emits the full row state so we just replace/insert it.
function onChange(type, patch) {
  const idx = guardrails.value.findIndex(g => g.guardrail_type === type);
  if (idx >= 0) {
    guardrails.value[idx] = { ...guardrails.value[idx], ...patch };
  } else {
    guardrails.value.push({ guardrail_type: type, ...patch });
  }
  dirty.value.add(type);
}

// Usa a config ATUAL da tela, inclusive o que ainda não foi salvo — testar depois de salvar
// não responderia a pergunta que o botão faz, que é se vale a pena ligar.
async function onTest(type, text) {
  const row = guardrails.value.find(g => g.guardrail_type === type);
  testState.value = { ...testState.value, [type]: { loading: true } };
  try {
    const data = await powerAgentsApi.guardrailTest(
      ctx.accountId(),
      ctx.agentId(),
      type,
      { text, config: row?.config || {} }
    );
    testState.value = {
      ...testState.value,
      [type]: {
        loading: false,
        matched: data.matched,
        text: data.text,
        term: data.term,
      },
    };
  } catch (e) {
    testState.value = {
      ...testState.value,
      [type]: { loading: false, error: true },
    };
  }
}

// Guardrails are structural (enforcement is a template upgrade), so a save also
// feeds the Publish pill.
// O que a pessoa edita aqui; a página avisa antes de perder (ver useRascunhoDaSecao).
const { suja, limpar, definirSalvar } = useRascunhoDaSecao(() => ({
  editados: [...dirty.value],
}));

async function save() {
  saving.value = true;
  try {
    const types = Array.from(dirty.value);
    const updates = await Promise.all(
      types.map(type => {
        const row = guardrails.value.find(g => g.guardrail_type === type);
        return powerAgentsApi.guardrailsUpsert(
          ctx.accountId(),
          ctx.agentId(),
          type,
          {
            enabled: row.enabled,
            stage: row.stage,
            action: row.action,
            block_message: row.block_message || '',
            config: row.config || {},
          }
        );
      })
    );
    updates.forEach((updated, i) => {
      const idx = guardrails.value.findIndex(
        g => g.guardrail_type === types[i]
      );
      guardrails.value[idx] = updated;
    });
    dirty.value = new Set();
    ctx.markPending();
    useAlert(t('POWER_AGENTS.SAVED'));
    limpar();
  } catch (e) {
    useAlert(t('POWER_AGENTS.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
}
definirSalvar(save);

onMounted(fetchGuardrails);
</script>

<template>
  <SectionShell
    data-busca="guardrails"
    :title="t('POWER_AGENTS.GUARDRAILS.TITLE')"
    :description="t('POWER_AGENTS.GUARDRAILS.DESCRIPTION')"
    :saving="saving"
    :suja="suja"
    @save="save"
  >
    <GuardrailsPanel
      :guardrails="guardrails"
      :rails-padrao="padrao"
      :loading="loading"
      :test-state="testState"
      @change="onChange"
      @test="onTest"
    />
  </SectionShell>
</template>
